import { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, Paperclip, MessageSquare, UploadCloud, FileText } from 'lucide-react';
import type { Town } from '@shared/types/town';

interface ContractTabProps {
    town: Town;
    onSave: (data: Record<string, unknown>) => void;
    onUpload: (file: File) => void;
    isSaving: boolean;
    isUploading: boolean;
    saveSignal?: number;
}

export default function ContractTab({ town, onSave, onUpload, isSaving, isUploading, saveSignal }: ContractTabProps) {
    const [deliveryDate, setDeliveryDate] = useState('');
    const [deliveryDateUnknown, setDeliveryDateUnknown] = useState(false);
    const [settingDate, setSettingDate] = useState('');
    const [settingDateUnknown, setSettingDateUnknown] = useState(false);
    const [contractStartDate, setContractStartDate] = useState('');
    const [contractedUntil, setContractedUntil] = useState('');
    const [notes, setNotes] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        setDeliveryDate(town.delivery_date ? town.delivery_date.slice(0, 10) : '');
        setDeliveryDateUnknown(Boolean(town.delivery_date_unknown));
        setSettingDate(town.setting_date ? town.setting_date.slice(0, 10) : '');
        setSettingDateUnknown(Boolean(town.setting_date_unknown));
        setContractStartDate(town.contract_start_date ? town.contract_start_date.slice(0, 10) : '');
        setContractedUntil(town.contracted_until ? town.contracted_until.slice(0, 10) : '');
        setNotes(town.notes ?? '');
    }, [town]);

    const doSave = useCallback(() => {
        onSave({
            delivery_date: deliveryDateUnknown ? null : (deliveryDate || null),
            delivery_date_unknown: deliveryDateUnknown,
            setting_date: settingDateUnknown ? null : (settingDate || null),
            setting_date_unknown: settingDateUnknown,
            contract_start_date: contractStartDate || null,
            contracted_until: contractedUntil || null,
            notes: notes || null,
        });
    }, [deliveryDate, deliveryDateUnknown, settingDate, settingDateUnknown, contractStartDate, contractedUntil, notes, onSave]);

    const doSaveRef = useRef(doSave);
    doSaveRef.current = doSave;

    // 하단 저장 버튼 및 탭 전환 시 저장 트리거
    const prevSignal = useRef(saveSignal ?? 0);
    useEffect(() => {
        if (saveSignal !== undefined && saveSignal > prevSignal.current) {
            doSaveRef.current();
        }
        prevSignal.current = saveSignal ?? 0;
    });

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            alert('PDF 파일만 업로드 가능합니다.');
            return;
        }
        onUpload(file);
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file) return;
        if (file.type !== 'application/pdf') {
            alert('PDF 파일만 업로드 가능합니다.');
            return;
        }
        onUpload(file);
    };

    // Contract period progress
    const getProgress = () => {
        if (!contractStartDate || !contractedUntil) return null;
        const start = new Date(contractStartDate).getTime();
        const end = new Date(contractedUntil).getTime();
        const now = Date.now();
        if (end <= start) return null;
        const total = end - start;
        const elapsed = now - start;
        const pct = Math.max(0, Math.min(100, (elapsed / total) * 100));
        const remaining = end - now;
        if (remaining <= 0) return { pct: 100, label: '만료됨' };
        const days = Math.floor(remaining / (1000 * 60 * 60 * 24));
        const years = Math.floor(days / 365);
        const months = Math.floor((days % 365) / 30);
        const parts: string[] = [];
        if (years > 0) parts.push(`${years}년`);
        if (months > 0) parts.push(`${months}개월`);
        if (parts.length === 0) parts.push(`${days}일`);
        return { pct, label: `남은 기간: ${parts.join(' ')}` };
    };

    const progress = getProgress();

    const inputClass = 'w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 focus:border-[#1ABB9C] transition';
    const labelClass = 'block text-[11px] font-medium text-[#ADB5BD] mb-1';

    return (
        <div>
            {/* 주요 일정 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Calendar className="w-3.5 h-3.5 text-[#1ABB9C]" /> 주요 일정
                </h3>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[11px] font-medium text-[#ADB5BD]">납품일</label>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={deliveryDateUnknown} onChange={(e) => { setDeliveryDateUnknown(e.target.checked); if (e.target.checked) setDeliveryDate(''); }} className="rounded border-gray-300 text-[#1ABB9C] w-3.5 h-3.5" />
                                <span className="text-[10px] text-[#ADB5BD]">확인불가</span>
                            </label>
                        </div>
                        {deliveryDateUnknown ? (
                            <div className="w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg bg-[#F5F7FA] text-[#ADB5BD]">확인불가</div>
                        ) : (
                            <input type="date" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} className={inputClass} />
                        )}
                    </div>
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className="text-[11px] font-medium text-[#ADB5BD]">세팅일</label>
                            <label className="inline-flex items-center gap-1 cursor-pointer">
                                <input type="checkbox" checked={settingDateUnknown} onChange={(e) => { setSettingDateUnknown(e.target.checked); if (e.target.checked) setSettingDate(''); }} className="rounded border-gray-300 text-[#1ABB9C] w-3.5 h-3.5" />
                                <span className="text-[10px] text-[#ADB5BD]">확인불가</span>
                            </label>
                        </div>
                        {settingDateUnknown ? (
                            <div className="w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg bg-[#F5F7FA] text-[#ADB5BD]">확인불가</div>
                        ) : (
                            <input type="date" value={settingDate} onChange={(e) => setSettingDate(e.target.value)} className={inputClass} />
                        )}
                    </div>
                    <div>
                        <label className={labelClass}>계약 시작일</label>
                        <input type="date" value={contractStartDate} onChange={(e) => setContractStartDate(e.target.value)} className={inputClass} />
                    </div>
                    <div>
                        <label className={labelClass}>계약 종료일</label>
                        <input type="date" value={contractedUntil} onChange={(e) => setContractedUntil(e.target.value)} className={inputClass} />
                    </div>
                </div>

                {progress && (
                    <div className="mt-4 p-3 bg-[#1ABB9C]/10/50 rounded-lg border border-[#1ABB9C]/20">
                        <div className="flex items-center justify-between text-[11px] text-[#73879C] mb-2">
                            <span>{contractStartDate}</span>
                            <span className="font-medium text-[#1ABB9C]">{progress.label}</span>
                            <span>{contractedUntil}</span>
                        </div>
                        <div className="w-full bg-blue-100 rounded-full h-2">
                            <div className="bg-[#1ABB9C] h-2 rounded-full transition-all" style={{ width: `${progress.pct}%` }} />
                        </div>
                    </div>
                )}
            </div>

            {/* 계약서 */}
            <div className="mb-7">
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5 text-[#1ABB9C]" /> 계약서
                </h3>
                <div
                    className="border-2 border-dashed border-[#E6E9ED] rounded-xl p-8 text-center hover:border-blue-400/40 transition cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                >
                    <UploadCloud className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-[#ADB5BD]">{isUploading ? '업로드 중...' : '파일을 드래그하거나 클릭하여 업로드'}</p>
                    <p className="text-[11px] text-gray-300 mt-1">PDF 파일만 가능, 최대 10MB</p>
                    <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleFileChange} className="hidden" />
                    {town.contract_file_path && (
                        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 bg-[#1ABB9C]/10 rounded-lg">
                            <FileText className="w-3.5 h-3.5 text-[#1ABB9C]" />
                            <a
                                href={`/api/towns/${town.id}/contract/download`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-[#1ABB9C] font-medium hover:underline"
                                onClick={(e) => e.stopPropagation()}
                            >
                                계약서 다운로드
                            </a>
                        </div>
                    )}
                </div>
            </div>

            {/* 특이사항 */}
            <div>
                <h3 className="text-xs font-bold text-[#2A3F54] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <MessageSquare className="w-3.5 h-3.5 text-[#1ABB9C]" /> 특이사항
                </h3>
                <textarea
                    rows={4}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-[#E6E9ED] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1ABB9C]/20 resize-y"
                    placeholder="특이사항을 자유롭게 입력하세요..."
                />
            </div>
        </div>
    );
}
